<template>
    <div ref="el" class="card-hand-adaptive" :class="{'card-hand-adaptive--open': isIntersecting}">
        <div class="card-hand-adaptive-inner" :class="{'ms-theme': msTheme, 'event-theme': eventTheme, 'brand-theme': brandTheme}">
            <div>
                <slot />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {ref} from 'vue';
    import {useIntersectionObserver} from '@malt/utils';

    defineProps<{msTheme?: boolean; eventTheme?: boolean; brandTheme?: boolean}>();

    const el = ref<HTMLElement | null>(null);
    const {isIntersecting} = useIntersectionObserver(el);
</script>

<style lang="scss">
    $card-width: 100%;
    $card-inner-width: 78%;
    $card-inner-border-radius: 250px;
    $card-transition: all 1.5s ease;
    $ratio: 0.85;

    .card-hand-adaptive {
        width: $card-width;
        height: max-content;
        aspect-ratio: $ratio;

        &--open {
            .card-hand-adaptive-inner {
                transform: translate3d(27%, 0, 0);
                &:after {
                    transform: translate3d(-7.5%, -1px, 0) rotate3d(0, 0, 1, -15deg);
                }
                &:before {
                    transform: translate3d(-15%, 0, 0) rotate3d(0, 0, 1, -33deg);
                }
            }
        }
    }

    .card-hand-adaptive-inner {
        position: relative;

        width: $card-inner-width;
        min-width: $card-inner-width;
        height: 100%;

        transition: $card-transition;
        transform: translate3d(0, 0, 0);

        > div,
        &:before,
        &:after {
            border-radius: $card-inner-border-radius;
            overflow: hidden;
            width: 100%;
            height: 100%;
        }
        &:before,
        &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            z-index: -1;
            transition: $card-transition;
            transform-origin: center;
            transform: translate3d(0, 0, 0) rotate3d(0, 0, 0, 0);
            will-change: transform;
        }
        img {
            backface-visibility: hidden;
            object-fit: cover;
            width: 100%;
            height: 100%;
        }
        &:after {
            background-color: var(--joy-color-primary-50);
        }
        &:before {
            background-color: var(--joy-color-primary-30);
        }
        &.ms-theme {
            &:after {
                background-color: var(--joy-color-secondary-30);
            }
            &:before {
                background-color: var(--joy-color-tertiary-70);
            }
        }
        &.brand-theme {
            &:after {
                background-color: var(--joy-color-primary-50);
            }
            &:before {
                background-color: var(--joy-color-brand-primary-30);
            }
        }
    }
</style>
